import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGolfApi,
  GolfOuting,
  GolfCourse,
  Team,
} from '../services/golfApi';
import { ArrowLeft, ChevronLeft, ChevronRight, Trophy, Save, Users } from 'lucide-react';
import toast from 'react-hot-toast';

const GolfScorecardPage: React.FC = () => {
  const { outingId } = useParams<{ outingId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const golfApi = useGolfApi();

  const [currentHole, setCurrentHole] = useState(1);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamScores, setTeamScores] = useState<{ [teamId: string]: { [holeNumber: number]: number } }>({});
  const [showSideGames, setShowSideGames] = useState(false);

  const { data: outing, isLoading: outingLoading } = useQuery({
    queryKey: ['golf', 'outing', outingId],
    queryFn: () => golfApi.getOuting(outingId!),
  });

  const { data: course } = useQuery({
    queryKey: ['golf', 'course', outing?.courseId],
    queryFn: () => golfApi.getCourse(outing!.courseId),
    enabled: !!outing?.courseId,
  });

  const { data: scores = [] } = useQuery({
    queryKey: ['golf', 'scores', outingId],
    queryFn: () => golfApi.getScores(outingId!),
    enabled: !!outingId,
  });

  const updateScoreMutation = useMutation({
    mutationFn: ({ teamId, holeNumber, score }: { teamId: string; holeNumber: number; score: number }) =>
      golfApi.updateScore(outingId!, teamId, holeNumber, score),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'scores', outingId] });
      queryClient.invalidateQueries({ queryKey: ['golf', 'outing', outingId, 'leaderboard'] });
      toast.success('Score saved');
    },
    onError: () => {
      toast.error('Failed to save score');
    },
  });

  // Load existing scores into state
  useEffect(() => {
    if (scores.length > 0) {
      const scoreMap: any = {};
      scores.forEach(score => {
        if (!scoreMap[score.teamId]) {
          scoreMap[score.teamId] = {};
        }
        scoreMap[score.teamId][score.holeNumber] = score.score;
      });
      setTeamScores(scoreMap);
    }
  }, [scores]);

  // Set initial selected team
  useEffect(() => {
    if (outing?.teams && outing.teams.length > 0 && !selectedTeam) {
      setSelectedTeam(outing.teams[0]);
    }
  }, [outing, selectedTeam]);

  if (outingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!outing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Outing not found</h2>
          <button
            onClick={() => navigate('/golf/outings')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Back to Outings
          </button>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Course data not available</h2>
          <button
            onClick={() => navigate(`/golf/outings/${outingId}`)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Back to Outing
          </button>
        </div>
      </div>
    );
  }

  if (!outing.teams || outing.teams.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center px-4">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Teams Created</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Teams must be created before you can enter scores.
          </p>
          <button
            onClick={() => navigate(`/golf/outings/${outingId}`)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Go to Outing to Create Teams
          </button>
        </div>
      </div>
    );
  }

  if (!selectedTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const currentHoleData = course.holes.find(h => h.holeNumber === currentHole);
  const currentScore = teamScores[selectedTeam.teamId]?.[currentHole] || 0;

  const handleScoreChange = (score: number) => {
    const newTeamScores = { ...teamScores };
    if (!newTeamScores[selectedTeam.teamId]) {
      newTeamScores[selectedTeam.teamId] = {};
    }
    newTeamScores[selectedTeam.teamId][currentHole] = score;
    setTeamScores(newTeamScores);
  };

  const handleSaveScore = () => {
    if (currentScore > 0) {
      updateScoreMutation.mutate({
        teamId: selectedTeam.teamId,
        holeNumber: currentHole,
        score: currentScore,
      });
    }
  };

  const handleNextHole = () => {
    if (currentScore > 0) {
      handleSaveScore();
    }
    if (currentHole < 18) {
      setCurrentHole(currentHole + 1);
    }
  };

  const handlePrevHole = () => {
    if (currentHole > 1) {
      setCurrentHole(currentHole - 1);
    }
  };

  const getTeamTotal = (teamId: string, throughHole: number = 18) => {
    const scores = teamScores[teamId] || {};
    return Object.entries(scores)
      .filter(([hole]) => parseInt(hole) <= throughHole)
      .reduce((sum, [, score]) => sum + score, 0);
  };

  const getTeamToPar = (teamId: string, throughHole: number = 18) => {
    const total = getTeamTotal(teamId, throughHole);
    const parThroughHole = course.holes
      .filter(h => h.holeNumber <= throughHole)
      .reduce((sum, h) => sum + h.par, 0);
    return total - parThroughHole;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate(`/golf/outings/${outingId}`)}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
              Exit
            </button>
            <button
              onClick={() => navigate(`/golf/outings/${outingId}/leaderboard`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Trophy className="h-4 w-4" />
              Leaderboard
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{outing.name}</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">{course.name}</p>
            </div>
            
            {/* Team Selector */}
            <select
              value={selectedTeam.teamId}
              onChange={(e) => {
                const team = outing.teams?.find(t => t.teamId === e.target.value);
                if (team) setSelectedTeam(team);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              {outing.teams?.map(team => (
                <option key={team.teamId} value={team.teamId}>
                  {team.teamName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Hole Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handlePrevHole}
              disabled={currentHole === 1}
              className="p-3 rounded-full bg-gray-100 dark:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-1">Hole {currentHole}</div>
              <div className="flex items-center justify-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span>Par {currentHoleData?.par}</span>
                <span>•</span>
                <span>{currentHoleData?.yardage} yards</span>
                <span>•</span>
                <span>HCP {currentHoleData?.handicap}</span>
              </div>
            </div>

            <button
              onClick={handleNextHole}
              disabled={currentHole === 18}
              className="p-3 rounded-full bg-green-600 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-green-700"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Score Entry */}
          <div>
            <label className="block text-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Team Score for Hole {currentHole}
            </label>
            <div className="grid grid-cols-8 gap-2 mb-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(score => {
                const par = currentHoleData?.par || 4;
                const diff = score - par;
                let colorClass = 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white';
                
                if (diff <= -2) colorClass = 'bg-blue-500 text-white'; // Eagle or better
                else if (diff === -1) colorClass = 'bg-blue-400 text-white'; // Birdie
                else if (diff === 0) colorClass = 'bg-gray-100 dark:bg-gray-700'; // Par
                else if (diff === 1) colorClass = 'bg-orange-400 text-white'; // Bogey
                else if (diff >= 2) colorClass = 'bg-red-500 text-white'; // Double+

                return (
                  <button
                    key={score}
                    onClick={() => handleScoreChange(score)}
                    className={`aspect-square rounded-lg text-xl font-bold transition-all ${
                      currentScore === score
                        ? 'ring-4 ring-green-600 scale-110'
                        : 'hover:scale-105'
                    } ${colorClass}`}
                  >
                    {score}
                  </button>
                );
              })}
            </div>

            {/* Score Legend */}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-600 dark:text-gray-400 mb-4">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-500"></div>
                Birdie/Eagle
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-orange-400"></div>
                Bogey
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-500"></div>
                Double+
              </span>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveScore}
              disabled={currentScore === 0 || updateScoreMutation.isPending}
              className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
            >
              <Save className="h-5 w-5" />
              {updateScoreMutation.isPending ? 'Saving...' : 'Save Score'}
            </button>
          </div>
        </div>

        {/* Current Team Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="h-5 w-5" />
            {selectedTeam.teamName}
          </h3>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {getTeamTotal(selectedTeam.teamId, currentHole)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${
                getTeamToPar(selectedTeam.teamId, currentHole) < 0 ? 'text-blue-600' :
                getTeamToPar(selectedTeam.teamId, currentHole) > 0 ? 'text-red-600' :
                'text-gray-900 dark:text-white'
              }`}>
                {getTeamToPar(selectedTeam.teamId, currentHole) > 0 ? '+' : ''}
                {getTeamToPar(selectedTeam.teamId, currentHole) || 'E'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">To Par</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {Object.keys(teamScores[selectedTeam.teamId] || {}).length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Holes</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Players:</div>
            {selectedTeam.players.map((player, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm py-1">
                <span className="text-gray-900 dark:text-white">{player.email}</span>
                <span className="text-gray-600 dark:text-gray-400">HCP: {player.handicap}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scorecard Grid */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Scorecard</h3>
          
          {/* Front 9 */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                  <th className="text-left py-2">Hole</th>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(h => (
                    <th key={h} className={`text-center py-2 ${currentHole === h ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
                      {h}
                    </th>
                  ))}
                  <th className="text-center py-2 font-bold">OUT</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-2 font-medium">Par</td>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(h => (
                    <td key={h} className={`text-center ${currentHole === h ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
                      {course.holes.find(hole => hole.holeNumber === h)?.par}
                    </td>
                  ))}
                  <td className="text-center font-bold">
                    {course.holes.filter(h => h.holeNumber <= 9).reduce((sum, h) => sum + h.par, 0)}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 font-medium">Score</td>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(h => (
                    <td key={h} className={`text-center font-bold ${currentHole === h ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
                      {teamScores[selectedTeam.teamId]?.[h] || '-'}
                    </td>
                  ))}
                  <td className="text-center font-bold">
                    {getTeamTotal(selectedTeam.teamId, 9) || '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Back 9 */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                  <th className="text-left py-2">Hole</th>
                  {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                    <th key={h} className={`text-center py-2 ${currentHole === h ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
                      {h}
                    </th>
                  ))}
                  <th className="text-center py-2 font-bold">IN</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-2 font-medium">Par</td>
                  {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                    <td key={h} className={`text-center ${currentHole === h ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
                      {course.holes.find(hole => hole.holeNumber === h)?.par}
                    </td>
                  ))}
                  <td className="text-center font-bold">
                    {course.holes.filter(h => h.holeNumber > 9).reduce((sum, h) => sum + h.par, 0)}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 font-medium">Score</td>
                  {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                    <td key={h} className={`text-center font-bold ${currentHole === h ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
                      {teamScores[selectedTeam.teamId]?.[h] || '-'}
                    </td>
                  ))}
                  <td className="text-center font-bold">
                    {(getTeamTotal(selectedTeam.teamId, 18) - getTeamTotal(selectedTeam.teamId, 9)) || '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className="mt-4 pt-4 border-t-2 border-gray-300 dark:border-gray-600 flex justify-between items-center">
            <span className="font-bold text-gray-900 dark:text-white">TOTAL</span>
            <span className="text-2xl font-bold text-green-600">
              {getTeamTotal(selectedTeam.teamId, 18) || '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2 overflow-x-auto">
            {Array.from({ length: 18 }, (_, i) => i + 1).map(h => (
              <button
                key={h}
                onClick={() => setCurrentHole(h)}
                className={`min-w-[40px] h-10 rounded-lg font-semibold transition-all ${
                  currentHole === h
                    ? 'bg-green-600 text-white scale-110'
                    : teamScores[selectedTeam.teamId]?.[h]
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                {h}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GolfScorecardPage;

